import React from 'react';
import CodeBlock from '../components/CodeBlock';

const Authentication = () => {
  return (
    <div className="page">
      <div className="page-header">
        <h1>🔐 Authentification</h1>
        <p>
          WhakUp utilise un système d'authentification à deux niveaux : API Key pour obtenir un JWT,
          puis JWT pour accéder aux endpoints protégés.
        </p>
      </div>

      <div className="section">
        <h2>1. Authentification avec API Key</h2>
        <p>
          Utilisez votre API Key pour obtenir un JWT valide pour toutes vos requêtes API.
        </p>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-post">POST</span>
            <span className="endpoint-path">/api/public/auth</span>
          </div>
          <div className="endpoint-description">
            Obtient un token JWT en utilisant une API Key
          </div>

          <h4>Headers requis:</h4>
          <table className="params-table">
            <thead>
              <tr>
                <th>Header</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>X-API-Key</code></td>
                <td>Votre API Key (whk_live_...)</td>
              </tr>
            </tbody>
          </table>

          <h4>Exemple de requête:</h4>
          <CodeBlock
            code={`curl -X POST http://127.0.0.1:8000/api/public/auth \\
  -H "X-API-Key: whk_live_votre_cle_api" \\
  -H "Content-Type: application/json"`}
            language="curl"
            showCopy={true}
          />

          <h4>Réponse:</h4>
          <CodeBlock
            code={`{
  "token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9...",
  "api_key_id": 1,
  "user": {
    "id": 123,
    "email": "user@exemple.com",
    "firstName": "John",
    "lastName": "Doe"
  },
  "workspaces": [
    {
      "id": 456,
      "title": "Mon Workspace 1",
      "role": "admin"
    },
    {
      "id": 789,
      "title": "Mon Workspace 2",
      "role": "user"
    }
  ]
}`}
            language="json"
            showCopy={true}
          />
        </div>
      </div>

      <div className="section">
        <h2>2. Utiliser le JWT</h2>
        <p>
          Une fois le JWT obtenu, utilisez-le dans l'header <code>Authorization</code> pour toutes vos requêtes API.
        </p>

        <CodeBlock
          code={`// Exemple d'utilisation
GET /api/v1/contacts
Headers:
  Authorization: Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9...
  X-Workspace-Id: 456
  Content-Type: application/json`}
          language="javascript"
          showCopy={true}
        />

        <div className="alert alert-info">
          <strong>💡 Note :</strong> Le JWT expire après 24 heures. Réauthentifiez-vous avec votre API Key pour obtenir un nouveau token.
        </div>
      </div>

      <div className="section">
        <h2>📝 Obtenir une API Key</h2>
        <p>
          Pour obtenir une API Key, contactez le support WhakUp ou utilisez votre tableau de bord administrateur.
          Une fois obtenue, vous pouvez l'utiliser pour vous authentifier via l'endpoint <code>/api/public/auth</code>.
        </p>
        <div className="alert alert-warning">
          <strong>⚠️ Important :</strong> Ne partagez jamais votre API Key publiquement. Conservez-la en sécurité.
        </div>
      </div>

      <div className="section">
        <h2>🔒 Sécurité</h2>
        <ul>
          <li>Ne partagez jamais votre API Key publiquement</li>
          <li>Stockez-la de manière sécurisée (variables d'environnement, secrets)</li>
          <li>Ne commitez jamais votre API Key dans votre code source</li>
          <li>Régénérez votre clé si elle est compromise</li>
        </ul>
      </div>
    </div>
  );
};

export default Authentication;
